import React from 'react';
import { Box, Typography, Alert, Stack } from '@mui/material';

/**
 * Guidance on washer hardness (HV) vs connection type — summary for engineers;
 * always verify against project NCCI, NA, and product standards.
 */
const WasherNotes = () => (
  <Stack spacing={2} sx={{ mt: 3, maxWidth: 900 }}>
    <Typography variant="h6">Washer hardness (HV) — connection type</Typography>

    <Alert severity="info">
      <Typography variant="subtitle2" gutterBottom>
        Non-preloaded bolting (e.g. ISO 15048) and ordinary structural joints
      </Typography>
      <Typography variant="body2" component="div">
        <Box component="p" sx={{ mt: 0, mb: 1.5 }}>
          For typical <strong>non-preloaded</strong> bearing-type joints, standard plain washers (e.g. ISO 7089) are
          usually adequate; product hardness is often around <strong>HV 200</strong> depending on grade, and there is
          generally <strong>no Eurocode requirement</strong> for HV 300 washers in that context. Match washers to the
          bolt/nut system and manufacturer data.
        </Box>
        <Box component="p" sx={{ m: 0 }}>
          <strong>Exception — single-lap shear and similar detailing:</strong> single-lap joints with only one bolt row
          are more sensitive to rotation and slip; check <strong>EN 1993-1-8 §3.6.1</strong> and your National Annex /
          NCCI for limits and rules. That is separate from ordinary non-preloaded bearing joints: if the connection is
          designed as <strong>Category B or C</strong> (preloaded, slip-resistant), you must use the{' '}
          <strong>specified preloaded system</strong> including <strong>hardened washers per EN 14399</strong>, not
          ordinary washers — see below.
        </Box>
      </Typography>
    </Alert>

    <Alert severity="warning">
      <Typography variant="subtitle2" gutterBottom>
        Preloaded / high-strength bolting (EN 14399, HV sets)
      </Typography>
      <Typography variant="body2">
        For preloaded assemblies intended to be slip-resistant at SLS/ULS, use the <strong>complete specified system</strong>{' '}
        (bolts, nuts, and <strong>hardened washers</strong>). EN 14399-6 defines hardened washers (typically{' '}
        <strong>HV 300</strong>) under head and nut for HV assemblies. Do not substitute washers from non-HV ranges
        without supplier approval and a checked design basis.
      </Typography>
    </Alert>

    <Typography variant="caption" color="text.secondary">
      Capacities in the Bolts tab use EN 1993-1-8 with γ<sub>M2</sub> = 1.25 (generic). National Annex factors may
      differ.
    </Typography>
  </Stack>
);

export default WasherNotes;
